import { h, Component, State, Prop, Watch, Element } from '@stencil/core';

import { isNumber, parseSvgString, getWidthSrc, getWidthSrcSet, observeImageSize } from '../../../services/utils';

@Component({
  tag: 'pk-image',
  styleUrl: './scss/pk-image.scss',
  scoped: true,
})
export class PkImage {
  // Own Properties
  // ====================
  private img: HTMLImageElement;
  private imgObserver: ResizeObserver;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkImageElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private _imgSrc: string;
  @State() private _imgSize: number;
  @State() private _svgSrc: string;
  @State() private _hasError: boolean;
  @State() private _fallbackImage: string;
  @State() private _useFallback: boolean = true;

  // Public Property API
  // ========================

  /** Alternative text description of the image image */
  @Prop() alt!: string;

  /** Specifies extra information about the image image,
   * this information is often shown as a tooltip text when the mouse moves over the adge image. */
  @Prop() imageTitle?: string;

  /** Fallback image to use when there's no image yet or until it loads */
  @Prop() fallbackImage?: string;

  /** Set the width of the image image */
  @Prop() width?: number;

  /** Set the height of the image image */
  @Prop() height?: number;

  /** Url to load the bagde image from (this can be also a base64 encoded image or an inline SVG string) */
  @Prop() src?: string;

  // Prop lifecycle events
  // =======================

  @Watch('src')
  handleSrcPropChange() {
    // check first if the src prop is an SVG string
    this._svgSrc = parseSvgString(this.src);

    this._imgSrc = this._svgSrc ? this._svgSrc : this.src;

    this._hasError = false;
  }

  @Watch('width')
  watchWidthProp() {
    if (isNumber(this.width)) {
      this.setImgSize(this.width);
    }
  }

  @Watch('_imgSrc')
  @Watch('_imgSize')
  @Watch('_hasError')
  setFallback() {
    this._useFallback = !this._imgSrc || this._hasError || !isNumber(this._imgSize);
  }

  @Watch('fallbackImage')
  handleFallbackImageProp() {
    this._fallbackImage = this.fallbackImage ?? 'https://img.uefa.com/imgml/void.svg';
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.handleSrcPropChange();
    this.watchWidthProp();
    this.handleFallbackImageProp();
  }

  disconnectedCallback() {
    this.imgObserver?.disconnect();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private connectRef = (el: HTMLImageElement) => {
    this.img = el;

    this.imgObserver = this.imgObserver ?? observeImageSize(this.img, this.setImgSize);

    if (!this._svgSrc) return;
    this.img.addEventListener('load', () => URL.revokeObjectURL(this._svgSrc), { once: true });
  };

  private setImgSize = (size: number) => {
    this._imgSize = size;
  };

  private onImageError = () => {
    if (this._useFallback) return;

    this._hasError = true;
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const src = this._useFallback ? this._fallbackImage : this._imgSrc;

    if (!src) return null;

    return (
      <img
        class={{ 'pk-image': true, 'min-h-[1px] min-w-[1px]': !this._hasError }}
        ref={this.connectRef}
        srcSet={getWidthSrcSet(src)}
        src={getWidthSrc(src)}
        alt={this.alt}
        loading="lazy"
        title={this.imageTitle}
        width={this.width > 0 ? this.width : undefined}
        height={this.height > 0 ? this.height : undefined}
        onError={this.onImageError}
        sizes={`${this._imgSize ?? 0}px`}
      />
    );
  }
}
